import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { ApiService } from 'src/app/services/api.service';
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { Urls } from 'src/app/lib/urls';

@Component({
  selector: 'app-features-auto-disable-logs',
  templateUrl: './features-auto-disable-logs.component.html',
  styleUrls: ['./features-auto-disable-logs.component.scss']
})
export class FeaturesAutoDisableLogsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Filter fields - stores the submitted filter criteria
  filterFields: any = null;
  fileName = '';

  // Data
  logsList: any[] = [];
  isLoading = false;

  // Pagination
  size = 10;
  total_pages = 0;
  current_page = 1;
  pages_array: number[] = [];
  total = 0;

  // Search
  search_keyword = '';
  searchKey: any = null;
  limitKey: any = null;

  // Sorting
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  constructor(
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private apiService: ApiService,
    private request: RequestsService,
    public paginationHelper: PaginationHelperService,
    public debounce: DebounceService,
    public dialogService: PopUpDialogService,
  ) {
    this.initializeDebounce();
  }

  ngOnInit(): void {
    console.log('Auto Disable Logs Feature Initialized');
    this.get_logs_list();
    
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on init:', err);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on destroy:', err);
    });
  }

  initializeDebounce(): void {
    // Search with debounce
    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.get_logs_list();
    }, 500);

    // Limit with debounce
    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.get_logs_list();
    }, 500);
  }

  get_logs_list(): void {
    this.isLoading = true;
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    const params = {
      search: this.search_keyword,
      sortBy: {
        [this.sort_column]: this.sort_type
      }
    };

    // Store filter params for export
    this.filterFields = params;
    localStorage.setItem('itemLists', JSON.stringify(params));

    this.pages_array = [];

    this.apiService.getDisableLogs(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            // Add showRemarks property to each log item
            this.logsList = response.result.data.map(log => ({
              ...log,
              showRemarks: false // Initially hide all remarks
            }));
            
            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages
            );
            this.total_pages = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Auto Disable Logs'
          );
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        }
      });
  }

  toggleRemarks(index: number): void {
    if (this.logsList[index]) {
      this.logsList[index].showRemarks = !this.logsList[index].showRemarks;
    }
  }

  // Uncomment and implement use the function if needed behavior like this in showing remarks
  // expandAllRemarks(): void {
  //   this.logsList.forEach(log => {
  //     if (log.remarks) {
  //       log.showRemarks = true;
  //     }
  //   });
  // }
  // collapseAllRemarks(): void {
  //   this.logsList.forEach(log => {
  //     log.showRemarks = false;
  //   });
  // }

  onLimitChangeFromComponent(event: any): void {
    this.size = parseInt(event.target.value, 10) || 10;
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.search_keyword = event.target.value;
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList(): void {
    this.current_page = 1;
    this.get_logs_list();
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => {
      console.error('Spinner show sort error:', err);
    });

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type = type;
    this.get_logs_list();
  }

  setPage(num: number): void {
    this.current_page = num;
    this.get_logs_list();
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      this.get_logs_list();
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      this.get_logs_list();
    }
  }

  print(): void {
    this.spinner.show();
    const current = new Date();
    this.filterFields = JSON.parse(localStorage.getItem('itemLists'));
    this.fileName = current.toLocaleDateString() + "-auto-disable-logs.xlsx";
    
    this.request.postBlob(Urls.exportAutoDisableLogs, this.filterFields).then(report => {
      const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
      const data = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = data;
      link.download = this.fileName;
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      setTimeout(() => {
        this.spinner.hide();
      }, 500);
    }).catch(error => {
      this.toastr.error('Failed to export logs', 'Error');
      this.spinner.hide();
    });
  }
}
